// src/pages/Quotation/components/QuotationTable.jsx
import React, { useEffect, useCallback, useState,useRef } from "react";
import { Table, Button, Popconfirm, Space, Checkbox, message, Input, Select } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import Big from "big.js";
import InputNumberField from "../../../components/Input/InputNumber";
import SearchListAutoComplete from "../../../components/SearchListAutoComplete";
import {
  addItemRow,
  deleteItemRow,
  updateItemRow,
  calculateTotals,
  setFooterValues,
  setServicePayCart,
  resetQuotation,
} from "../quotationSlice";
import SharedFooter from "../../../components/DataTableFooter/SharedFooter";
import { getCurrencySymbol } from "../../../lib/currency";
import { fetchItems } from "../../../store/commonThunks/lookups";
import { selectItems } from "../../../store/commonSelectors/lookupsSelectors";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchProductsList } from "../../Products/productsThunks";
import SearchAutoComplete from "../../../components/SearchAutoComplete";
import { selectGetProductsList } from "../../Products/productsSelectors";
import ProductQuickDrawer from "../../Product_manage/components/drawers/ProductQuickDrawer";
import { getItemDisplayName } from "../../../utils";

const QuotationTable = ({ authInfo, institution }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const currency = authInfo?.currency || "AED";
  const symbol = getCurrencySymbol(currency);
  const { footerValues, itemCart, taxMode, servicePayCart, conversionRate, conversionCurrency } =
    useSelector((s) => s.quotation);

  const customer = useSelector((s) => s.quotation.customer);
  const items = useSelector(selectItems);
  const isPO = useSelector((s) => s.purchaseEntry.isPO);
  const isIndia = authInfo?.country?.toLowerCase() === "india";
  const isUAE = authInfo?.country === "United Arab Emirates";
const isDrugStore = authInfo?.company_type === "Drug Store" && isUAE;
const isPharmacy = authInfo?.company_type === "Pharmacy";
  const taxLabel = isIndia ? "GST" : "VAT";
  const gstEnabled = authInfo?.gst_enabled === "yes";
  const showTax = !isIndia || gstEnabled;
const getRateForCustomer = (item, partyType, isMohItem = false) => {
  if (isMohItem) {
    return parseFloat(item.moh_invoice_rate) || parseFloat(item.sale_rate) || 0;
  }
  switch (partyType) {
    case 'wholesaler':
      return parseFloat(item.wholesaler_rate) || parseFloat(item.sale_rate) || 0;
    case 'retailer':
      return parseFloat(item.retailer_rate)   || parseFloat(item.sale_rate) || 0;
    case 'distributor':
      return parseFloat(item.distributor_rate)|| parseFloat(item.sale_rate) || 0;
    case 'corporate':
      return parseFloat(item.corporate_rate)  || parseFloat(item.sale_rate) || 0;
    default:
      return parseFloat(item.sale_rate) || 0;
  }
};

const isRateUpdateInProgress = useRef(false);
const lookups = useSelector((state) => state.lookups);
useEffect(() => {
  if (isRateUpdateInProgress.current) return;

  const bonusItemIds = new Set(
    itemCart
      .filter((row) => row.bonus && row.item_id)
      .map((row) => row.item_id)
  );

  // ✅ No bonus rows = nothing to recalculate, exit immediately
  if (bonusItemIds.size === 0) return;

  let needsUpdate = false;

  itemCart.forEach((row) => {
    if (!row.bonus && row.item_id) {
      const shouldUseMoh = bonusItemIds.has(row.item_id);
      const fullProduct = items.find((i) => i.item_id === row.item_id) || {};
      const partyType = customer?.party_type || "general";

      const expectedRate = shouldUseMoh
        ? parseFloat(fullProduct.moh_invoice_rate) || parseFloat(fullProduct.sale_rate) || 0
        : getRateForCustomer(fullProduct, partyType, false);

      if (parseFloat(row.rate) !== parseFloat(expectedRate)) {
        needsUpdate = true;
        isRateUpdateInProgress.current = true;
        dispatch(updateItemRow({ key: row.key, updates: { rate: expectedRate } }));
      }
    }
  });

  if (needsUpdate) {
    setTimeout(() => { isRateUpdateInProgress.current = false; }, 100);
  }
}, [itemCart]);
  const [productDrawer, setProductDrawer] = useState({ open: false, rowKey: null, });

  const fetchProducts = useCallback(
    (searchText) => {
      dispatch(
        fetchProductsList({
          query: searchText,
          search: true,
          partyType: "general",
          includeServices: true,
        })
      );
    },
    [dispatch]
  );
const RateSelector = ({ record, handleUpdate, lookups, conversionRate, currency }) => {
  const warnedRef = useRef(false);

  const allowEdit = lookups?.institutionData?.allow_product_rate_change === "yes";
  const isFullyLocked = !record?.item_id;
  const isManualEditAllowed = allowEdit && !isFullyLocked;

  const displayRate = record.bonus
    ? 0
    : parseFloat(
        conversionRate
          ? (record?.rate || 0) * conversionRate
          : record?.rate || 0
      );

  return (
    <InputNumberField
      min={0}
      step={0.01}
      precision={2}
      value={displayRate}
      disabled={isFullyLocked}
      onChange={(v) => {
        if (isManualEditAllowed) {
          handleUpdate(record.key, "rate", v);
        }
      }}
      onKeyDown={(e) => {
        if (!isManualEditAllowed) {
          e.preventDefault();
          if (!warnedRef.current) {
            message.warning("Enable 'Allow Product Rate Edit' in settings to edit manually");
            warnedRef.current = true;
          }
        }
      }}
      onBlur={() => { warnedRef.current = false; }}
      style={{
        cursor: isFullyLocked ? "not-allowed" : isManualEditAllowed ? "text" : "pointer",
        background: isManualEditAllowed ? "white" : "#fafafa",
        color: isManualEditAllowed ? "inherit" : "#888",
        caretColor: isManualEditAllowed ? "auto" : "transparent",
      }}
    />
  );
};
  const handleUpdate = (rowKey, field, value) => {
    dispatch(updateItemRow({ key: rowKey, updates: { [field]: value } }));
    // dispatch(calculateTotals({ authInfo }));
  };

  //  const handleUpdate = (rowKey, field, value) => {
  //     const row = itemCart.find((r) => r.key === rowKey);
  //     const updates = { [field]: value };

  //     if (["quantity", "rate", "discount", "tax_per", "bonus"].includes(field)) {
  //       const qty = Number(field === "quantity" ? value : row.quantity) || 0;
  //       const rate = Number(field === "rate" ? value : row.rate) || 0;
  //       const tax_per = Number(field === "tax_per" ? value : row.tax_per) || 0;
  //       const discount = Number(field === "discount" ? value : row.discount) || 0;

  //       let line = qty * rate;
  //       if (taxMode === "individual") {
  //         const discountAmt = (discount / 100) * line || 0;
  //         line -= discountAmt;
  //         const taxAmt = (line * tax_per) / 100 || 0;
  //         line += taxAmt;
  //       }

  //       updates.line_total = parseFloat(
  //         conversionRate ? line * conversionRate : line
  //       ).toFixed(2);
  //     }

  //     dispatch(updateItemRow({ rowKey, updates }));
  //     dispatch(calculateTotals({ authInfo }));
  //   };

  const handleFooterChange = (value, field) => {
    let updatedFooter = { ...footerValues };
    if (field === "payServiceCart") {
      const newPaidAmount = value.reduce(
        (sum, item) => sum + parseFloat(item.tran_amount || 0),
        0
      );
      updatedFooter = {
        ...updatedFooter,
        paidAmount: newPaidAmount,
        dueAmount: (
          parseFloat(updatedFooter.grandTotal || 0) - newPaidAmount
        ).toFixed(2),
      };

      const normalizedCart = value.map((v) => ({
        to_acc_id: v.to_acc_id ?? v.acc_id ?? null,
        to_acc_name: v.to_acc_name ?? v.acc_name ?? "",
        tran_amount: v.tran_amount ?? "",
      }));

      dispatch(setServicePayCart(normalizedCart));
    } else if (field === "paidAmount") {
      updatedFooter.paidAmount = Number(value) || 0;
      updatedFooter.dueAmount = (
        parseFloat(updatedFooter.grandTotal || 0) - updatedFooter.paidAmount
      ).toFixed(2);
    } else if (field === "showTermsAndConditions") {

    updatedFooter.showTermsAndConditions = value;

    if (value) {
      updatedFooter.termsConditions =
        institution?.quotation_terms || "";

      updatedFooter.paymentTerms =
        institution?.quotation_payment_terms || "";
    }
  } else {
      updatedFooter[field] = value;
    }

    dispatch(setFooterValues(updatedFooter));
    // dispatch(calculateTotals({ authInfo }));
  };

  const handleAddRow = () => {
    const newRow = {
      key: Date.now() + Math.random(),
      item_id: null,
      product: "",
      qty: null,
      rate: 0,
      discount: null,
      discountAmt: null,
      discountMode: "percent",
      item_description: "",
      showDescription: false,
      tax_per: 0,
      unit_symbol: "",
      bonus: false,
      isBlank: false,
    };
    dispatch(addItemRow(newRow));
    // dispatch(calculateTotals({ authInfo }));
  };

  useEffect(() => {
    if (itemCart.length === 0 || itemCart[itemCart.length - 1].item_id) {
      dispatch(
        addItemRow({
          key: Date.now(),
          item_id: null,
          product: "",
          product_display: "",
          qty: null,
          rate: null,
          discount: null,
          discountAmt: null,
          discountMode: "percent",
          item_description: "",
          showDescription: false,
          tax_per: 0,
          unit_symbol: "",
             bonus: false,
          isBlank: true,
        })
      );
    }
  }, [itemCart, dispatch]);

  // Don't preserve state if not required on pageload
  useEffect(() => {
    return () => {
      // reset only if NOT preserving
      if (!location.state?.preserveQuotation) {
        dispatch(resetQuotation());
      }
    };
  }, [dispatch, location.state]);

  const columns = [
    {
      title: "No.",
      dataIndex: "serial",
      key: "serial",
      width: 60,
      render: (text, record, index) => index + 1,
    },
    {
  title: "Bonus",
  dataIndex: "bonus",
  width: 50,
  render: (_, record) => (
    <Checkbox
      checked={record.bonus}
      onChange={(e) => {
        const isBonus = e.target.checked;
        dispatch(
          updateItemRow({
            key: record.key,
            updates: {
              bonus: isBonus,
              rate: isBonus ? 0 : record.rate,
              discount: 0,
              tax_per: isBonus ? 0 : record.tax_per,
            },
          })
        );
      }}
      style={{
        transform: "scale(1.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    />
  ),
},
    {
      title: "Product / Scan Barcode",
      dataIndex: "product",
      render: (_, record) => (
        // <SearchListAutoComplete
        //   placeholder="Item"
        //   style={{ minWidth: 180 }}
        //   rules={[{ required: true, message: "Select an Item" }]}
        //   optionsList={(items || []).map((i) => ({
        //     sale_id: i.item_id,
        //     display_text: i.item_name,
        //     raw: i,
        //   }))}
        //   value={
        //     record.product
        //       ? { value: record.product, label: record.product } // ✅ Pre-fill selected text
        //       : undefined
        //   }
        //   onFocus={() => dispatch(fetchItems())}
        //   onSelect={(obj) => {
        //     if (!obj?.raw) return;
        //     const item = obj.raw;

        //     if (item === "add_new") {
        //       navigate("/item-manage", { state: { preserveQuotation: true } }); // or customer creation page
        //       return;
        //     }

        //     dispatch(
        //       updateItemRow({
        //         key: record.key,
        //         updates: {
        //           item_id: item.item_id,
        //           product: item.item_name,
        //           rate: parseFloat(item.sale_rate || 0).toFixed(2),
        //           unit_symbol: item.unit_symbol,
        //           discount: item.discount,
        //           tax_per: item.tax_per,
        //           qty: 1,
        //           item_code: item.item_code ?? 0,
        //           unit_id: item.unit_id || 0,
        //           unit_name: item.unit_name || "",
        //           per_unit_id: item.unit_id || 0,
        //           per_unit_symbol: item.unit_symbol || "",
        //           per_conversion: item.conversion || 1,
        //           warehouse_id: item.warehouse_id || 0,
        //           warehouse_name: item.warehouse_name || "",
        //           base_unit_name: item.base_unit_name || "",
        //           conversion: item.conversion || 1,
        //           units: item.units || [],
        //           isBlank: false,
        //         },
        //       })
        //     );
        //     dispatch(calculateTotals({ authInfo }));

        //     if (record.isBlank) {
        //       dispatch(
        //         addItemRow({
        //           key: Date.now() + Math.random(),
        //           product: "",
        //           qty: 0,
        //           rate: 0,
        //           discount: 0,
        //           tax_per: 0,
        //           unit_symbol: "",
        //           isBlank: true,
        //         })
        //       );
        //     }
        //   }}
        // />
        <div>
        <SearchAutoComplete
          searchThunk={(text) => fetchProducts(text)}
          resultSelector={selectGetProductsList}
          // Asset items are Purchase Bill-only (non-stock) -- never offer for sale.
          filterResult={(results) => results.filter((r) => r.is_asset !== "yes")}
          value={
            record.item_id
              ? {
                // `product` is also what gets saved as this row's item_name
                // (see utils/quotation.js), so it stays the plain name -- the
                // fuller "name - code - barcode" label only lives in
                // product_display, used for showing/picking in this field.
                display_text: record.product_display || record.product,
                item_name: record.product,
              }
              : undefined
          }

          // onSelect={async (value) => {
          //   if (!value || value === null) return;
          //   if (value === "add_new") {
          //     navigate(`/inventory/product/addNewItem`, {
          //       state: { from: "/invoice" },
          //     });
          //     return;
          //   }
          //   handleUpdate(record.key, "item_id", value.item_id);
          //   handleUpdate(record.key, "product", value.item_name);
          //   handleUpdate(record.key, "rate", value.purchase_rate ?? 0);
          //   handleUpdate(record.key, "tax_per", value.tax_per ?? 0);
          //   handleUpdate(record.key, "unit_id", value.unit_id ?? 0);
          //   handleUpdate(record.key, "unit_name", value.unit_name || "");
          //   handleUpdate(record.key, "unit_symbol", value.unit_symbol || "");
          //   handleUpdate(record.key, "units", value.units || []);

          //   handleUpdate(record.key, "batch_name", "");
          //   handleUpdate(record.key, "batch_id", 0);
          //   handleUpdate(record.key, "batch_expiry", null);
          //   handleUpdate(record.key, "is_expiry_disable", false);

          //   dispatch(fetchBatchAvailability(value.item_id));
          // }}
        onSelect={(obj) => {
  // 🔥 FULL CLEAR
  if (!obj) {
    dispatch(
      updateItemRow({
        key: record.key,
        updates: {
          item_id: null,
          product: "",
          product_display: "",
          qty: null,
          rate: null,
          discount: 0,
          item_description: "",
          showDescription: false,
          tax_per: 0,
          unit_symbol: "",
          units: [],
          conversion: 1,
          per_conversion: 1,
          isBlank: true,
          autoSpawned: false,
          // ✅ FIXED: was referencing undefined `item`
          sale_rate: null,
          moh_invoice_rate: null,
          wholesaler_rate: null,
          retailer_rate: null,
          distributor_rate: null,
          corporate_rate: null,
        },
      })
    );
    return;
  }

  const item = obj;

  if (item === "add_new") {
    setProductDrawer({ open: true, rowKey: record.key });
    return;
  }

  // ✅ Check if this item already exists as a bonus row
  const existsAsBonus = itemCart.some(
    (row) =>
      row.key !== record.key &&
      row.item_id === item.item_id &&
      row.bonus === true
  );

  // ✅ Also check if current row itself is a bonus row
  const currentRowIsBonus = record.bonus === true;

  const partyType = customer?.party_type || "general";
  const shouldUseMoh = existsAsBonus || currentRowIsBonus;

  const baseRate = shouldUseMoh
    ? parseFloat(item.moh_invoice_rate) || parseFloat(item.sale_rate) || 0
    : getRateForCustomer(item, partyType, false);

  dispatch(
    updateItemRow({
      key: record.key,
      updates: {
        item_id: item.item_id,
        product: item.item_name,
        product_display: item.display_text || item.item_name,
        rate: parseFloat(baseRate || 0),  // ✅ use computed rate
        unit_symbol: item.unit_symbol,
        discount: (isDrugStore || isPharmacy)
          ? parseFloat(item.discount_per || 0)
          : 0,
        tax_per: currentRowIsBonus ? 0 : item.tax_per,  // ✅ 0 tax for bonus
        qty: 0,
        // Pre-fill the line description from the product's Narration / Description
        // (editable afterwards). Replaces the previous product's text.
        item_description: String(item.narration ?? "").trim(),
        showDescription: !!String(item.narration ?? "").trim(),
        item_code: item.item_code ?? 0,
        unit_id: item.unit_id || 0,
        unit_name: item.unit_name || "",
        per_unit_id: item.unit_id || 0,
        per_unit_symbol: item.unit_symbol || "",
        per_conversion: item.conversion || 1,
        warehouse_id: item.warehouse_id || 0,
        warehouse_name: item.warehouse_name || "",
        base_unit_name: item.base_unit_name || "",
        conversion: item.conversion || 1,
        units: item.units || [],
        isBlank: false,
        autoSpawned: false,
        // ✅ store all rate variants for useEffect to work
        sale_rate: item.sale_rate,
        moh_invoice_rate: item.moh_invoice_rate,
        wholesaler_rate: item.wholesaler_rate,
        retailer_rate: item.retailer_rate,
        distributor_rate: item.distributor_rate,
        corporate_rate: item.corporate_rate,
      },
    })
  );

  if (record.isBlank && record.autoSpawned) {
    dispatch(
      addItemRow({
        key: Date.now() + Math.random(),
        product: "",
        qty: 0,
        rate: 0,
        discount: 0,
        item_description: "",
        showDescription: false,
        tax_per: 0,
        unit_symbol: "",
        bonus: false,
        isBlank: true,
        autoSpawned: true,
      })
    );
  }
}}
          placeholder={"products"}
        />
       {record.item_id && (
        <Button
          type="link"
          size="small"
          style={{ padding: 0 }}
          onClick={() =>
            handleUpdate(
              record.key,
              "showDescription",
              !record.showDescription
            )
          }
        >
          {record.showDescription
            ? "Hide Description"
            : "Add Description"}
        </Button>
      )}

      {record.showDescription && (
        <Input.TextArea
          rows={2}
          placeholder="Add item description"
          value={record.item_description}
          onChange={(e) =>
            handleUpdate(
              record.key,
              "item_description",
              e.target.value
            )
          }
        />
      )} 
        </div>
      ),
    },
    {
      title: "Qty",
      dataIndex: "qty",
      width: 100,
      render: (_, record) => (
        <InputNumberField
          min={0}
          value={record.qty ?? null}
          precision={1}       
          step={0.5}      
          onChange={(v) => handleUpdate(record.key, "qty", v === null ? null : v)}
          onClick={() => {
            if (record.qty === 0) {
              handleUpdate(record.key, "qty", null);
            }
          }}
          onFocus={() => {
            if (record.qty === 0) {
              handleUpdate(record.key, "qty", null);
            }
          }}
        />
      ),
    },
{
  title: "Rate",
  dataIndex: "rate",
  width: 120,
  render: (_, record) => (
    <RateSelector
      key={`${record.key}-${conversionRate}`}
      record={record}
      handleUpdate={handleUpdate}
      lookups={lookups}
      conversionRate={conversionRate}
      currency={currency}
    />
  ),
},
    ...(taxMode === "on_item"
      ? [
        {
          title: "Discount",
          dataIndex: "discount",
          width: 160,
          render: (_, record) => {
            const mode = record.discountMode === "amount" ? "amount" : "percent";
            const lineTotal = Big(record.qty || record.item_qty || 0).times(record.rate || record.item_rate || 0);
            // One merged compound field (number input + unit-select addon)
            // instead of a separate Select stacked above the number input.
            const modeSelect = (
              <Select
                size="small"
                style={{ width: 65 }}
                value={mode}
                options={[
                  { label: "%", value: "percent" },
                  { label: symbol, value: "amount" },
                ]}
                onChange={(v) => handleUpdate(record.key, "discountMode", v)}
              />
            );
            return mode === "amount" ? (
              <InputNumberField
                min={0}
                max={Number(lineTotal)}
                data-testid="discount-amount-input"
                value={record.discountAmt}
                onChange={(v) => handleUpdate(record.key, "discountAmt", v)}
                addonAfter={modeSelect}
              />
            ) : (
              <InputNumberField
                min={0}
                max={100}
                data-testid="discount-input"
                value={record.discount}
                onFocus={() => {
                  if (Number(record.discount) === 0) {
                    handleUpdate(record.key, "discount", null);
                  }
                }}
                onChange={(v) => handleUpdate(record.key, "discount", v)}
                addonAfter={modeSelect}
              />
            );
          },
        },

      ]
      : []),
    ...(taxMode === "on_item" && showTax
      ? [
        {
          title: `${taxLabel} %`,
          dataIndex: "tax_per",
          width: 120,
          render: (_, record) => (
            <span>
              {Number(record.tax_per || record.item_tax_per || 0).toFixed(2)}
            </span>
          ),
        },
      ]
      : []),
    {
      title: "Line Total",
      render: (_, r) => {
        if (r.bonus) return "0.00";

        const qty = Big(r.qty || r.item_qty || 0);
        const rate = Big(r.rate || r.item_rate || 0);

        let line = qty.times(rate);

        if (taxMode === "on_item") {

          const discountMode = r.discountMode === "amount" ? "amount" : "percent";
          let discountAmt;
          if (discountMode === "amount") {
            const raw = Big(r.discountAmt || 0);
            discountAmt = raw.gt(line) ? line : raw.lt(0) ? Big(0) : raw;
          } else {
            const discountPct = Big(r.discount || 0);
            discountAmt = line.times(discountPct).div(100);
          }

          line = line.minus(discountAmt);

          const taxPer = showTax ? Big(r.tax_per || r.item_tax_per || 0) : Big(0);
          const taxAmt = line.times(taxPer).div(100);

          line = line.plus(taxAmt);
        }

        if (conversionRate) {
          line = line.times(Big(conversionRate));
        }

        return line.toFixed(2);
      },
    }
  ];

  columns.push({
    title: "Action",
    render: (_, record) => (
      <Popconfirm
        title="Delete row?"
        onConfirm={() => dispatch(deleteItemRow(record.key))}
      >
        <Button danger size="small" icon={<DeleteOutlined />} />
      </Popconfirm>
    ),
  });

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 10,
        }}
      >
        <div />

        {/* <Button type="dashed" onClick={handleAddRow} icon={<PlusOutlined />} style={{ marginBottom: 8 }}>
            Add Row
          </Button> */}
      </div>

      <div style={{ width: "100%", maxWidth: "1300px", overflowX: "auto" }}>
        <Table
          dataSource={itemCart}
          columns={columns}
          pagination={false}
          rowKey="key"
          scroll={{ x: "max-content" }}
          footer={() => (
            <SharedFooter
              footerValues={footerValues}
              dataSource={itemCart}
              onFooterChange={handleFooterChange}
              pageType="quotation"
              taxMode={taxMode}
              servicePayCart={servicePayCart}
              conversionRate={conversionRate}
              conversionCurrency={conversionCurrency}
              institution={institution}
            />
          )}
        />
      </div>

      <ProductQuickDrawer
        open={productDrawer.open}
        onClose={() => setProductDrawer({ open: false, rowKey: null })}
        onCreated={(product) => {
          if (!product) return;

          const productValue = {
            ...product,
            item_id: product.item_id,
            item_name: product.item_name,
            display_text: product.item_name,
            tax_per: product.tax_per,
            sale_rate: product.sale_rate,
            raw: product,
          };



          // ✅ update table row product
          dispatch(
            updateItemRow({
              key: productDrawer.rowKey,
              updates: {
                item_id: productValue.item_id,
                product: productValue.item_name,
                product_display: getItemDisplayName(productValue),
                rate: parseFloat(productValue.sale_rate || 0).toFixed(2),
                unit_symbol: productValue.unit_symbol,
                discount: (isDrugStore || isPharmacy)   // ✅ changed
        ? parseFloat(productValue.discount_per || 0)
        : 0,
                tax_per: productValue.tax_per,
                qty: 0,
                item_code: productValue.item_code ?? 0,
                unit_id: productValue.unit_id || 0,
                unit_name: productValue.unit_name || "",
                per_unit_id: productValue.unit_id || 0,
                per_unit_symbol: productValue.unit_symbol || "",
                per_conversion: productValue.conversion || 1,
                warehouse_id: productValue.warehouse_id || 0,
                warehouse_name: productValue.warehouse_name || "",
                base_unit_name: productValue.base_unit_name || "",
                conversion: productValue.conversion || 1,
                units: productValue.units || [],
                item_description: String(productValue.narration ?? "").trim(),
                showDescription: !!String(productValue.narration ?? "").trim(),
                isBlank: false,
                autoSpawned: false,
              },
            })
          );

          // dispatch(calculateTotals({ authInfo }));

          // 2️⃣ 🔥 Re-trigger product search so dropdown list refreshes
          dispatch(
            fetchProductsList({
              query: product.item_name,
              search: true,
              partyType: "general",
              includeServices: true,
            })
          );

          // ✅ CLOSE DRAWER FIRST
          setProductDrawer({ open: false, rowKey: null });


        }}
      />

    </>
  );
};

export default QuotationTable;
